import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, User, Phone, Mail, UserPlus, Calendar,
  Ruler, Dumbbell, Activity, Heart, MapPin, ClipboardList,
  Package, CheckCircle2, Clock, Truck, ShieldAlert,
  CreditCard, Tag, Landmark, Wallet, Percent, Receipt, X, ChevronRight, CheckCircle
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const DetailCard = ({ icon: Icon, label, value, isDark, themeColor }) => (
  <div className={`p-4 rounded-2xl border transition-all flex items-start gap-4 ${isDark ? 'bg-white/5 border-white/5 hover:bg-white/10 shadow-inner' : 'bg-white border-gray-100 shadow-sm hover:shadow-md'}`}>
    {Icon && (
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-white/5' : ''}`} style={!isDark ? { backgroundColor: `${themeColor}15` } : {}}>
        <Icon className="w-5 h-5" style={{ color: themeColor }} />
      </div>
    )}
    <div className="min-w-0 flex-1">
      <p className={`text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{label}</p>
      <p className={`text-sm font-black mt-1 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{value || '—'}</p>
    </div>
  </div>
);

function VerifiedFailedOrderDetail() {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === '#FFFFFF';
  const navigate = useNavigate();

  const displayValue = (value) => value === null || value === undefined || value === '' || value === 0 ? '-/-' : value;

  return (
    <div className={`min-h-screen p-6 font-sans transition-colors duration-500 ${isDark ? 'bg-[#0F172A]' : 'bg-[#F8FAFC]'}`}>

      {/* Top Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className={`p-3 rounded-2xl transition-all shadow-lg active:scale-90 ${isDark ? 'bg-white/5 text-white hover:bg-white/10' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
            <ArrowLeft size={24} style={{ color: currentTheme.color }} />
          </button>
          <div>
            <h1 className={`text-2xl font-black uppercase tracking-[0.2em] ${isDark ? 'text-white' : 'text-gray-800'}`}>Verification Failed</h1>
            <p className={`text-[10px] font-black uppercase tracking-widest mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Order Details & Timeline</p>
          </div>
        </div>
        <div className={`px-6 py-2 rounded-xl text-xs font-black uppercase tracking-[0.2em] shadow-xl text-white bg-red-500`}>
          Verification Failed
        </div>
      </div>

      <div className="flex gap-8 max-w-[1600px] mx-auto">
        {/* Main Content Area */}
        <div className="flex-1 space-y-6">

          {/* Order Summary Ribbon */}
          <div className={`rounded-3xl overflow-hidden flex items-stretch shadow-2xl border transition-all ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
            <div className="flex-1 grid grid-cols-3 p-8 gap-8">
              <div>
                <p className={`text-[10px] font-black uppercase tracking-widest mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Order ID</p>
                <p className={`text-xl font-black ${isDark ? 'text-white' : 'text-gray-900'}`}>OID5532</p>
              </div>
              <div className={`border-l pl-8 ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                <p className={`text-[10px] font-black uppercase tracking-widest mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Order Date</p>
                <p className={`text-lg font-black ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>23/03/2024</p>
              </div>
              <div className={`border-l pl-8 ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                <p className={`text-[10px] font-black uppercase tracking-widest mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Primary Product</p>
                <p className={`text-lg font-black truncate ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>3A Piles Care Capsules</p>
              </div>
            </div>
            <div className="px-10 flex flex-col justify-center items-center text-white min-w-[200px]" style={{ backgroundColor: currentTheme.color }}>
              <span className="text-[10px] font-black uppercase tracking-[0.2em] opacity-70 mb-1">Rest Amount</span>
              <span className="text-3xl font-black">₹ 1500</span>
            </div>
          </div>

          {/* Customer Details */}
          <div className={`p-8 rounded-3xl shadow-xl border transition-all ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
            <div className="flex justify-between items-center mb-8">
              <h3 className={`text-sm font-black uppercase tracking-[0.2em] ${isDark ? 'text-white' : 'text-gray-800'}`}>Customer Details</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <DetailCard icon={User} label="Customer name" value="Vinay Chaurasia" isDark={isDark} themeColor={currentTheme.color} />
              <DetailCard icon={Phone} label="Contact number" value="9876543210" isDark={isDark} themeColor={currentTheme.color} />
              <DetailCard icon={Phone} label="Alternate number" value="-/-" isDark={isDark} themeColor={currentTheme.color} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <DetailCard icon={Mail} label="Email" value="vinay@abc.com" isDark={isDark} themeColor={currentTheme.color} />
              <DetailCard icon={UserPlus} label="Gender" value="Male" isDark={isDark} themeColor={currentTheme.color} />
              <DetailCard icon={Calendar} label="Age" value="32" isDark={isDark} themeColor={currentTheme.color} />
              <DetailCard icon={Ruler} label="Height" value="5.6" isDark={isDark} themeColor={currentTheme.color} />
            </div>
          </div>

          {/* Basic Details */}
          <div className={`p-8 rounded-3xl shadow-xl border transition-all ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
            <h3 className={`text-sm font-black uppercase tracking-[0.2em] mb-8 ${isDark ? 'text-white' : 'text-gray-800'}`}>Health Profile</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { icon: Dumbbell, label: 'Weight', value: '72 kg', color: 'text-red-400' },
                  { icon: Activity, label: 'Actual Problem', value: 'Diabetes', color: 'text-blue-400' },
                  { icon: ShieldAlert, label: 'Medical Treatment', value: 'Insulin Therapy', color: 'text-emerald-400' },
                  { icon: Heart, label: 'Marital Status', value: 'Married', color: 'text-purple-400' }
                ].map((item, idx) => (
                  <div key={idx} className={`p-5 rounded-2xl border transition-all ${isDark ? 'bg-white/5 border-white/5' : 'bg-gray-50 border-gray-100'}`}>
                    <item.icon className={`w-6 h-6 mb-3 ${item.color}`} />
                    <p className={`text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{item.label}</p>
                    <p className={`text-sm font-black mt-1 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{item.value}</p>
                  </div>
                ))}
            </div>
          </div>

          {/* Address Section */}
          <div className={`p-8 rounded-3xl shadow-xl border transition-all ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
            <h3 className={`text-sm font-black uppercase tracking-[0.2em] mb-6 ${isDark ? 'text-white' : 'text-gray-800'}`}>Delivery Address</h3>
            <div className={`p-6 rounded-2xl mb-8 flex items-start gap-4 border transition-all ${isDark ? 'bg-white/5 border-white/5' : 'bg-blue-50 border-blue-100'}`}>
               <MapPin className="w-8 h-8 mt-1 flex-shrink-0" style={{ color: currentTheme.color }} />
               <p className={`text-lg font-black tracking-wide ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>H. No. B-429, sec 44, Noida, Gautam buddha nagar 201301, U.P.</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
               {[
                 { label: 'Pin Code', value: '201301' },
                 { label: 'Post Office', value: 'Noida Sec 44' },
                 { label: 'District', value: 'G.B Nagar' },
                 { label: 'State', value: 'Uttar Pradesh' }
               ].map((item, idx) => (
                 <div key={idx}>
                    <p className={`text-[10px] font-black uppercase tracking-widest mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{item.label}</p>
                    <p className={`text-sm font-black ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{item.value}</p>
                 </div>
               ))}
            </div>
          </div>

          {/* Remarks */}
          <div className={`p-8 rounded-3xl shadow-xl border transition-all ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
            <h3 className={`text-sm font-black uppercase tracking-[0.2em] mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`}>Remarks</h3>
            <div className={`p-4 rounded-xl border italic ${isDark ? 'bg-white/5 border-white/5 text-gray-400' : 'bg-gray-50 border-gray-100 text-gray-600'}`}>
              "New Order Created - Verification Pending"
            </div>
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="w-[420px] space-y-6">

          {/* Product Details Sidebar Card */}
          <div className={`p-8 rounded-3xl shadow-xl border transition-all ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
             <h3 className={`text-sm font-black uppercase tracking-[0.2em] mb-6 ${isDark ? 'text-white' : 'text-gray-800'}`}>Product Selection</h3>
             <div className={`flex gap-4 items-center p-4 rounded-2xl border transition-all ${isDark ? 'bg-white/2 border-white/5' : 'bg-gray-50 border-gray-100'}`}>
                <div className={`w-16 h-16 rounded-xl flex items-center justify-center border ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
                   <Package className={`w-8 h-8 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                </div>
                <div className="flex-1 min-w-0">
                   <p className={`font-black text-sm truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>3A Piles Care Capsules</p>
                   <p className={`text-[10px] font-black uppercase tracking-widest mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>35 Capsules / Pack</p>
                   <div className="flex justify-between items-center mt-3">
                       <p className="text-[10px] font-black uppercase tracking-widest text-gray-500">Qty: <span className={isDark ? 'text-white' : 'text-gray-900'}>1</span></p>
                       <p className="text-xl font-black" style={{ color: currentTheme.color }}>₹ 1500</p>
                    </div>
                </div>
             </div>

             {/* Order Timeline */}
             <div className="mt-12 relative ml-4 space-y-10">
                <div className={`absolute left-[15px] top-2 bottom-2 w-0.5 border-l-2 border-dashed transition-all ${isDark ? 'border-white/10' : 'border-gray-200'}`} />

                {[
                  { label: 'Order by', name: 'Nitesh Singh', date: '03/12/2025, 01:15 pm', status: 'done' },
                  { label: 'Verified by', name: 'Arjun Singh', date: '03/12/2025, 01:45 pm', status: 'done' },
                  { label: 'Dispatch by', name: '-/-', date: 'Pending', status: 'pending' },
                  { label: 'NDR by', name: '-/-', date: 'Pending', status: 'pending' }
                ].map((step, idx) => (
                  <div key={idx} className={`relative flex items-start gap-6 transition-all ${step.status === 'pending' ? 'opacity-30 grayscale' : ''}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center z-10 transition-all shadow-lg ${step.status === 'done' ? 'scale-110' : 'scale-90 bg-gray-200'}`}
                         style={step.status === 'done' ? { backgroundColor: currentTheme.color, boxShadow: `0 0 15px ${currentTheme.color}66` } : {}}>
                      {step.status === 'done' ? <CheckCircle size={16} className="text-white" /> : <div className="w-2 h-2 rounded-full bg-gray-400" />}
                    </div>
                    <div className="flex-1">
                      <p className={`text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{step.date}</p>
                      <div className={`mt-2 p-4 rounded-2xl border transition-all ${step.status === 'done' && isDark ? 'bg-white/5 border-white/10' : (step.status === 'done' ? 'bg-gray-50 border-gray-100' : 'border-transparent')}`}>
                        <p className={`text-[10px] font-black uppercase tracking-widest mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{step.label}</p>
                        <p className={`text-sm font-black ${isDark ? 'text-white' : 'text-gray-900'}`}>{step.name}</p>
                      </div>
                    </div>
                  </div>
                ))}
             </div>
          </div>

          {/* Small Status Boxes Grid */}
          <div className="grid grid-cols-2 gap-4">
             {[
               { label: 'Shipping', value: 'Free Shipping' },
               { label: 'Payment Type', value: 'Part Payment' },
               { label: 'Payment Mode', value: 'Online' },
               { label: 'Prepaid', value: '₹ 100', color: 'text-emerald-500' },
               { label: 'Discount', value: '₹ 200', color: 'text-emerald-500' },
               { label: 'Status', value: 'Verified', color: 'text-emerald-500' }
             ].map((item, idx) => (
               <div key={idx} className={`p-4 rounded-2xl border transition-all ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
                  <p className={`text-[10px] font-black uppercase tracking-widest mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{item.label}</p>
                  <p className={`text-xs font-black truncate ${item.color || (isDark ? 'text-gray-200' : 'text-gray-800')}`}>{item.value}</p>
               </div>
             ))}
          </div>

          {/* Summary Card */}
          <div className={`p-8 rounded-3xl shadow-2xl border transition-all ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
             <h3 className={`text-sm font-black uppercase tracking-[0.2em] mb-8 ${isDark ? 'text-white' : 'text-gray-800'}`}>Financial Summary</h3>
             <div className="space-y-4">
                {[
                  { label: 'Sub Total', value: '₹1500', color: isDark ? 'text-gray-200' : 'text-gray-900' },
                  { label: 'Prepaid Amount', value: '₹100', color: 'text-emerald-500' },
                  { label: 'Total Discounts', value: '₹200', color: 'text-emerald-500' },
                  { label: 'Shipping Charges', value: 'Free', color: 'text-emerald-500' }
                ].map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center">
                    <span className={`text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{item.label}</span>
                    <span className={`text-sm font-black ${item.color}`}>{item.value}</span>
                  </div>
                ))}
                <div className="pt-6 border-t border-dashed mt-4" style={{ borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}>
                  <div className="flex justify-between items-center">
                    <span className={`text-xs font-black uppercase tracking-[0.2em] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Grand Total:</span>
                    <span className="text-3xl font-black" style={{ color: currentTheme.color }}>₹1200</span>
                  </div>
                </div>
             </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VerifiedFailedOrderDetail;
